import { SYSTEM_ID } from '@bandido/shared/constants';
import { CaseHistoryResponseDto } from '@bandido/shared/dtos';
import {
  Box,
  Fade,
  Modal,
  Skeleton,
  Stack,
  Typography,
  useTheme,
  Link,
} from '@mui/material';
import { forwardRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InsertDriveFile, WhatsApp } from '@mui/icons-material';
import dayjs from 'dayjs';
import { CONFIG } from '@frontend/config';

import AudioWave from '@frontend/features/common/components/AudioWave';
import AIReasoningAccordion from '@frontend/features/common/components/AIReasoningAccordion';
import { downloadFile } from '@frontend/features/common/queries';

import { ContentStack, MessageContainer } from '../EmailItem/styled';
import { MessageProps } from '../../types';
import { Timeline } from '../Timeline';
import { useAuth } from '@frontend/features/auth/hooks/useAuth';
import { UserRoles } from '@bandido/shared/types';
import { useCaseContext } from '@frontend/features/cases/context';
import { interpolateCasePlaceholders } from '../../helpers';

interface WhatsappItemProps extends MessageProps {
  history: CaseHistoryResponseDto;
  isCurrentUser: boolean;
}

const RenderDocument = ({ fileId, caption, filename }: { fileId: string, caption?: string, filename?: string }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [documentSource, setDocumentSource] = useState<string>();
  const theme = useTheme();
  const { t } = useTranslation();

  useEffect(() => {
    setIsLoading(true);
    if (fileId) {
      downloadFile(+fileId, true)
        .then((url: any) => {
          if (!url) return;
          setDocumentSource(url);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [fileId]);

  return <>
    {isLoading ? (
      <Skeleton variant="rectangular" width="100%" sx={{ borderRadius: 1, height: 80 }} />
    ) : documentSource ? (
      <Box
        maxWidth={360}
        display="flex"
        flexDirection="column"
        alignItems="center"
        padding={2}
        border={1}
        borderColor="divider"
        borderRadius={1}
        bgcolor="grey.100"
      >
        <InsertDriveFile
          sx={{
            fontSize: 48,
            color: theme.palette.primary.main
          }}
        />
        <Box mt={1} width="100%" textAlign="center">
          <Link
            href={documentSource}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: theme.palette.primary.main,
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
          >
            <Typography variant="body2" fontWeight="medium">
              {filename || t('common.view')}
            </Typography>
          </Link>
        </Box>
      </Box>
    ) : null}
    {caption && (
      <Typography variant="body2" fontWeight="medium">
        {caption}
      </Typography>
    )}
  </>

}

const RenderVideo: React.FC<{ fileId: string }> = ({
  fileId,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [videoSource, setVideoSource] = useState<string>();

  useEffect(() => {
    setIsLoading(true);
    if (fileId) {
      downloadFile(+fileId)
        .then((url: any) => {
          if (!url) return;
          setVideoSource(url);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [fileId]);

  return (

    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      width="100%"
    >
      {isLoading ? (
        <Skeleton variant="rectangular" width="100%" sx={{ borderRadius: 1, height: 200 }} />
      ) : videoSource ? (
        <Box maxWidth={360}>
          <video
            controls
            width="100%"
            style={{ borderRadius: 8 }}
          >
            <source src={videoSource} type="video/mp4" />
          </video>
        </Box>
      ) : null}
    </Box>
  );
};

export const WhatsappItem = forwardRef<HTMLDivElement, WhatsappItemProps>((props, ref) => {
  const [openModal, setOpenModal] = useState(false);
  const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false);
  const { t } = useTranslation();
  const { myProfile } = useAuth();
  const theme = useTheme();
  const { caseData } = useCaseContext();

  const { whatsappToPhone, whatsappCaption: caption, whatsappFilename: filename, whatsappType, whatsappText: text, whatsappSenderName, whatsappIaReasoning } = props.history;
  const type = whatsappType as string;
  const inbound = whatsappSenderName === SYSTEM_ID;

  const authorName =
    `${props.history.authorFirstName || ''} ${props.history.authorLastName || ''}`.trim() || props.history.callFrom;

  const interpolatedText = interpolateCasePlaceholders(text, {
    clientFirstName: caseData?.clientFirstName,
    clientLastName: caseData?.clientLastName,
    agentFirstName: caseData?.agentFirstName,
    agentLastName: caseData?.agentLastName,
  });
  const interpolatedCaption = interpolateCasePlaceholders(caption, {
    clientFirstName: caseData?.clientFirstName,
    clientLastName: caseData?.clientLastName,
    agentFirstName: caseData?.agentFirstName,
    agentLastName: caseData?.agentLastName,
  });

  const toggleModal = () => setOpenModal((prev) => !prev);

  const handleAccordionChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedAccordion(isExpanded ? panel : false);
  };

  const renderContent = () => {
    switch (type) {
      case 'text':
        return (
          <Typography variant="body2">{interpolatedText}</Typography>
        );

      case 'contacts':
        return (
          <Typography variant="body2">{interpolatedText}</Typography>
        );

      case 'template':
        return (
          <Typography variant="body2">{interpolatedText}</Typography>

        );

      case 'sticker':
        return text && (
          <Box
            component="img"
            src={text}
            alt="Sticker"
            sx={{
              maxWidth: 200,
              maxHeight: 200,
              objectFit: "contain",
            }}
          />
        );

      case 'audio':
        return text && (
          <AudioWave fileId={+text} />
        );

      case 'video':
        return text && (
          <RenderVideo fileId={text} />
        );

      case 'image':
        return text && (
          <Box>
            <Box
              component="img"
              src={text}
              alt="Image"
              sx={{
                maxWidth: 200,
                maxHeight: 200,
                objectFit: "contain",
                cursor: "pointer"
              }}
              onClick={toggleModal}
            />
            {interpolatedCaption && (
              <Typography
                variant="body2"
                sx={{
                  mt: 1,
                  wordBreak: 'break-word',
                  whiteSpace: 'pre-wrap'
                }}
              >
                {interpolatedCaption.split('\n').map((line, index) => {
                  // Check if line contains URL
                  const urlRegex = /(https?:\/\/[^\s]+)/g;
                  const parts = line.split(urlRegex);

                  return (
                    <span key={index}>
                      {parts.map((part, partIndex) => {
                        if (urlRegex.test(part)) {
                          return (
                            <Link
                              key={partIndex}
                              href={part}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ color: 'primary.main' }}
                            >
                              {part}
                            </Link>
                          );
                        }
                        return <span key={partIndex}>{part}</span>;
                      })}
                      {index < interpolatedCaption.split('\n').length - 1 && <br />}
                    </span>
                  );
                })}
              </Typography>
            )}
          </Box>
        );

      case 'document':
        return text && (
          <RenderDocument fileId={text} caption={interpolatedCaption} filename={filename as string} />
        );

      case 'location':
        if (text) {
          const [latitude, longitude] = text.split("|").map(coord => parseFloat(coord.trim()));
          const mapImageUrl = `https://maps.googleapis.com/maps/api/staticmap?center=${latitude},${longitude}&zoom=15&size=300x200&markers=color:red%7C${latitude},${longitude}&key=${CONFIG.GOOGLE_KEY}`;
          const googleMapsUrl = `https://www.google.com/maps?q=${latitude},${longitude}`;
          const handleMapClick = () => {
            window.open(googleMapsUrl, '_blank');
          };
          return text && (
            <Box
              component="img"
              src={mapImageUrl}
              alt={t('whatsapp.chatLocation.location')}
              sx={{
                borderRadius: 1,
                cursor: 'pointer',
                maxWidth: '300px'
              }}
              onClick={handleMapClick}
            />
          );
        }
        return null

      default:
        return null;
    }
  };


  const createdAtTime = dayjs(props.history.createdAt).format('HH:mm');
  const userIsAdmin = myProfile?.roles.includes(UserRoles.ADMINS);

  return (
    <MessageContainer ref={ref} sx={{ justifyContent: inbound ? 'flex-end' : 'flex-start' }}>
      <Modal
        open={openModal}
        onClose={toggleModal}
        closeAfterTransition
        sx={{
          overflow: 'auto',
          display: 'flex',
          justifyContent: 'center',
          p: 4,
        }}
      >
        <Fade in={openModal}>
          <Box
            sx={{
              bgcolor: 'background.paper',
              boxShadow: 24,
              borderRadius: 1,
              p: 2,
              minHeight: 'fit-content',
            }}
          >
            <Box
              component="img"
              src={text}
              alt="Image"
              sx={{
                display: 'block',
                height: 'auto',
                width: 'auto',
              }}
            />
          </Box>
        </Fade>
      </Modal>
      <Stack
        order={inbound ? 1 : 2}
        mr={(theme) => theme.spacing(inbound ? 2.5 : 0)}
        ml={(theme) => theme.spacing(inbound ? 0 : 2.5)}
      >
        <Stack
          className="history-author"
          display="flex"
          flexDirection="row"
          justifyContent={inbound ? 'flex-end' : 'flex-start'}
          alignItems="center"
          pb={1}
          gap={2}
        >
          <Box gap={1} display="flex">
            <Box display="flex" gap={0.8} alignItems="center">
              <Typography variant="subtitle2">{authorName}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('whatsapp.client.sentTo')}
              </Typography>
              <Typography variant="subtitle2">{whatsappToPhone}</Typography>
            </Box>
          </Box>
        </Stack>
        <Stack
          maxWidth={600}
          minWidth={300}
          borderRadius={2}
          border={(theme) => `1px solid ${theme.palette.divider}`}
          display="flex"
          flexDirection="column"
          justifyContent="flex-end"
        >
          <Box
            display="flex"
            padding={(theme) => theme.spacing(1, 2)}
            flexDirection="row"
            borderRadius="16px 16px 0 0"
            alignItems="center"
            gap={1}
            bgcolor={(theme) => theme.palette.success.light}
          >
            <WhatsApp sx={{ color: 'common.white' }} />
            <Typography color="background.paper" variant="subtitle2">
              {t('cases.view.content.whatsapp')}
            </Typography>
          </Box>
          <Stack borderRadius="0 0 16px 16px" bgcolor={(theme) => theme.palette.background.paper} padding={2}>
            <ContentStack sx={{ backgroundColor: (theme) => theme.palette.grey[100], padding: 1 }}>
              {renderContent()}
            </ContentStack>
            {whatsappIaReasoning && userIsAdmin && (
              <AIReasoningAccordion
                expanded={expandedAccordion === 'ia-reasoning'}
                onChange={handleAccordionChange('ia-reasoning')}
                panelId="ia-reasoning"
                htmlContent={whatsappIaReasoning}
              />
            )}
          </Stack>
        </Stack>
        <Typography textAlign="right" pt={1} color="text.disabled" variant="subtitle2">
          {createdAtTime}
        </Typography>
      </Stack>

      <Timeline
        isCurrentUser={inbound}
        icon={WhatsApp}
        color="background.paper"
        background={theme.palette.success.light}
      />
    </MessageContainer>
  );
});

WhatsappItem.displayName = 'WhatsappItem';
